import { InputNumber } from "antd";
import React, { useState } from "react";
import styled from "styled-components";
import Slider from "./Slider";
import { SliderTitle, SliderTitleProps } from "./SliderTitle";
import { colors } from "src/theme/colors";
import { line, noSteppers, radii, space, withAlpha } from "src/theme/ui";

interface AmountSliderProps {
  className?: string;
  titles: SliderTitleProps;
  value: number;
  name: string;
  max: number;
  onChange: (newValue: number) => void;
}

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: ${space.lg}px;

  @media (max-width: 570px) {
    flex-direction: column-reverse;
    align-items: stretch;
    gap: ${space.sm}px;
  }
`;

const Track = styled.div`
  flex: 1;
  min-width: 0;
`;

/**
 * The ends of the range, named.
 *
 * A slider with an empty rail and the handle against its left stop says
 * nothing about what one pixel of travel is worth: the same picture means
 * "nought of a thousand" and "nought of a million". The two bounds are the
 * scale, and without them the coarse control is unusable and the reader is
 * pushed into the box beside it — which is the precise control, and the one
 * that needs them least.
 */
const Bounds = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: -2px;

  span {
    font-size: 9.5px;
    font-weight: 600;
    letter-spacing: 0.03em;
    font-variant-numeric: tabular-nums;
    color: ${colors.gray[400]};
  }
`;

/**
 * The precise control.
 *
 * A neutral field with the accent kept for focus, in place of a periwinkle
 * box with a periwinkle border — an always-on accent marks nothing, because
 * marking is a contrast and every field had it.
 *
 * The currency is a real prefix element rather than a `::before` on the
 * wrapper offset with a left padding on the input: that trick puts the glyph
 * in a layer the input does not know about, so a long figure runs under its
 * own dollar sign.
 */
export const StyledAmountInput = styled(InputNumber)`
  ${noSteppers}

  &.ant-input-number {
    width: 148px;
    height: 34px;
    border-radius: ${radii.control}px;
    border: 1px solid ${line.hairline};
    background: ${colors.white};
    box-shadow: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  &.ant-input-number:hover {
    border-color: ${colors.gray[300]};
  }

  &.ant-input-number-focused {
    border-color: ${colors.primary[500]};
    box-shadow: 0 0 0 3px ${withAlpha(colors.primary[500], 0.14)};
  }

  .ant-input-number-input-wrap {
    display: flex;
    align-items: center;
    height: 100%;
  }

  .ant-input-number-input {
    height: 32px;
    padding: 0 10px 0 24px;
    font-size: 13.5px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: ${colors.gray[900]};
  }

  @media (max-width: 570px) {
    &.ant-input-number {
      width: 100%;
    }
  }
`;

const Money = styled.div`
  position: relative;
  flex: none;

  /* The unit, in the field but not in the way of the digits. */
  &::before {
    content: "$";
    position: absolute;
    top: 50%;
    left: 10px;
    transform: translateY(-50%);
    z-index: 1;
    font-size: 13px;
    font-weight: 600;
    color: ${colors.gray[400]};
    pointer-events: none;
  }
`;

const grouped = (value: number) =>
  `$${Math.round(value).toLocaleString("en-US")}`;

const AmountSlider: React.FC<AmountSliderProps> = ({
  titles,
  value,
  max,
  name,
  onChange,
  className,
}) => {
  const [amount, setAmount] = useState(value);

  return (
    <Field className={className}>
      <SliderTitle {...titles} />
      <Controls>
        <Track>
          <Slider
            value={amount}
            min={0}
            defaultValue={0}
            max={max}
            tooltipVisible={false}
            onChange={setAmount}
            onAfterChange={onChange}
          />
          <Bounds>
            <span>$0</span>
            <span>{grouped(max)}</span>
          </Bounds>
        </Track>
        <Money>
          <StyledAmountInput
            value={amount}
            name={name}
            min={0}
            /* No max on the box, deliberately: the slider's ceiling is the
               ceiling of the COARSE control, and typing a figure above it
               was allowed before. Clamping here would silently rewrite a
               number the reader had entered. */
            controls={false}
            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            parser={(v) => v!.replace(/\$\s?|(,*)/g, "")}
            onChange={(next) => {
              setAmount(next as number);
              onChange(next as number);
            }}
          />
        </Money>
      </Controls>
    </Field>
  );
};

export default AmountSlider;
